import React, { useState, useEffect, useMemo } from 'react';
import styles from './NSPDPanel.module.css';
import { 
    SearchIcon, GlobeIcon, PlusIcon, InfoIcon, XCircleIcon, 
    LayersIcon, RotateIcon, SettingsIcon, CheckCircleIcon,
    AlertTriangleIcon, CrosshairIcon, UploadIcon
} from '../../../components/Icon';
import * as turf from '@turf/turf';
import { toast } from 'react-toastify';

const NSPDPanel = ({ isVisible, onClose, activeTool, setActiveTool, nspdInfo, mapData, onMapSelectRequest, onImport, onFeatureSelect }) => {
    const [searchMode, setSearchMode] = useState('grid'); // Default to grid for mass import
    const [cadNumber, setCadNumber] = useState('');
    const [cadNumbers, setCadNumbers] = useState(''); // Для массового импорта
    const [quarterId, setQuarterId] = useState('');
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [gridResults, setGridResults] = useState([]);
    const [error, setError] = useState(null);
    const [extracting, setExtracting] = useState(false);
    const [importProgress, setImportProgress] = useState(null); // { current, total }
    const [checkingUpdates, setCheckingUpdates] = useState(false);
    const [checkingProgress, setCheckingProgress] = useState({ current: 0, total: 0 });
    const [updateStatus, setUpdateStatus] = useState({}); // { cadNum: { hasUpdate: bool, newData: obj, diff: string } }

    // Локальные объекты в текущей точке
    const localObjectsAtPoint = useMemo(() => {
        if (!nspdInfo || !nspdInfo.coordinates || !mapData) return [];
        const point = [nspdInfo.coordinates.lng, nspdInfo.coordinates.lat];
        
        return mapData.features.filter(f => {
            if (!f.geometry) return false;
            
            // Если полигон - проверяем вхождение точки
            if (f.geometry.type === 'Polygon' || f.geometry.type === 'MultiPolygon') {
                try {
                    return turf.booleanPointInPolygon(point, f.geometry);
                } catch (e) { return false; }
            }
            
            // Если точка или линия - проверяем расстояние (радиус 20 метров)
            try {
                const distance = turf.distance(point, turf.centroid(f.geometry), { units: 'kilometers' });
                return distance <= 0.02; // 20 метров
            } catch (e) { return false; }
        }).slice(0, 10);
    }, [nspdInfo, mapData]);
    
    // Все сохраненные объекты с кадастровыми номерами
    const savedObjects = useMemo(() => {
        if (!mapData || !mapData.features) return [];
        return mapData.features.filter(f => f.properties.cadastral_number);
    }, [mapData]);

    const normalizeCad = (num) => {
        if (!num) return '';
        return String(num).replace(/\s+/g, '').replace(/[\(\)]/g, '');
    };

    const getLocalMatch = (cadNum) => {
        if (!cadNum || !mapData?.features) return null;
        const target = normalizeCad(cadNum);
        return mapData.features.find(f => normalizeCad(f.properties.cadastral_number) === target);
    };

    const localMatch = useMemo(() => {
        const cad = result?.kad_number || result?.data?.features?.[0]?.properties?.cad_num || result?.data?.features?.[0]?.properties?.options?.cad_num;
        return getLocalMatch(cad);
    }, [result, mapData]);

    const getItemStatus = (item) => {
        const cadNum = item.kad_number;
        const local = getLocalMatch(cadNum);
        if (!local) return { label: 'Создать', type: 'new', icon: <PlusIcon size={16} /> };
        
        // Сравниваем площадь для определения обновлений
        const nspdProps = item.data?.features?.[0]?.properties || item.data?.features?.[0]?.properties?.options || {};
        const nspdArea = parseFloat(nspdProps.area_value || nspdProps.area || 0);
        const localArea = parseFloat(local.properties.area || 0);
        
        const areaDiff = Math.abs(nspdArea - localArea) > 0.1;
        
        if (areaDiff) return { label: 'Обновить', type: 'update', icon: <RotateIcon size={16} /> };
        return { label: 'Актуально', type: 'current', icon: <CheckCircleIcon size={16} /> };
    };

    // Эффект для поиска по клику на карте
    useEffect(() => {
        if (nspdInfo && nspdInfo.coordinates && isVisible) {
            setSearchMode('point');
            
            // Проверяем, не является ли это уже загруженным результатом для этой точки
            const isAlreadyLoaded = result && 
                                    result._searchPoint && 
                                    Math.abs(result._searchPoint.lat - nspdInfo.coordinates.lat) < 0.000001 &&
                                    Math.abs(result._searchPoint.lng - nspdInfo.coordinates.lng) < 0.000001;
            
            if (!isAlreadyLoaded) {
                handleSearchByCoords(nspdInfo.coordinates.lat, nspdInfo.coordinates.lng);
            }
        }
    }, [nspdInfo, isVisible, result]);

    const handleSearchByCoords = async (lat, lon) => {
        setLoading(true);
        setError(null);
        setResult(null);
        setGridResults([]);
        setCadNumber('');

        try {
            const response = await fetch(`/api/m/nspd-integration/search-coords?lat=${lat}&lon=${lon}`);
            const data = await response.json();

            if (data.error) {
                setError(data.error);
            } else {
                // Добавляем координаты поиска в результат для кеширования
                const enrichedData = {
                    ...data,
                    _searchPoint: { lat, lng: lon }
                };
                setResult(enrichedData);
                if (data.kad_number) setCadNumber(data.kad_number);
            }
        } catch (err) {
            setError('Ошибка при поиске по координатам');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = async (e, targetNum) => {
        if (e && e.preventDefault) e.preventDefault();
        const query = targetNum || cadNumber;
        if (!query) return;

        setLoading(true);
        setError(null);
        setResult(null);
        setGridResults([]);

        try {
            const response = await fetch(`/api/m/nspd-integration/search?q=${encodeURIComponent(query)}`);
            const data = await response.json();

            if (data.error) {
                setError(data.error);
            } else {
                setResult(data);
            }
        } catch (err) {
            setError('Ошибка при запросе к НСПД');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const [progressLogs, setProgressLogs] = useState([]);
    const [stats, setStats] = useState({ total: 0, new: 0 });
    const consoleRef = React.useRef(null);

    // Автопрокрутка консоли логов
    useEffect(() => {
        if (consoleRef.current) {
            consoleRef.current.scrollTop = consoleRef.current.scrollHeight;
        }
    }, [progressLogs]);

    const handleFileUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setExtracting(true);
        const formData = new FormData();
        formData.append('file', file);

        try {
            const response = await fetch('/api/m/nspd-integration/extract-ids', {
                method: 'POST',
                body: formData
            });
            const data = await response.json();

            if (data.ids && data.ids.length > 0) {
                setCadNumbers(data.ids.join(', '));
                toast.success(`Извлечено ${data.ids.length} кадастровых номеров`);
            } else if (data.error) {
                toast.error(data.error);
            } else {
                toast.info('В архиве не найдено кадастровых номеров');
            }
        } catch (err) {
            console.error(err);
            toast.error('Ошибка при обработке файла');
        } finally {
            setExtracting(false);
            e.target.value = null; // Reset input
        }
    };

    const handleCheckAllUpdates = async () => {
        if (savedObjects.length === 0) return;
        setCheckingUpdates(true);
        setCheckingProgress({ current: 0, total: savedObjects.length });
        const newUpdateStatus = { ...updateStatus };
        let updatedCount = 0;

        toast.info(`Начинаем проверку ${savedObjects.length} объектов...`);

        try {
            for (let i = 0; i < savedObjects.length; i++) {
                const obj = savedObjects[i];
                const cadNum = obj.properties.cadastral_number;
                if (!cadNum) continue;

                // Небольшая задержка чтобы не спамить
                if (i > 0) await new Promise(r => setTimeout(r, 500));

                try {
                    const response = await fetch(`/api/m/nspd-integration/search?q=${encodeURIComponent(cadNum)}`);
                    const data = await response.json();

                    if (!data.error) {
                        const nspdObj = data.data?.features?.[0];
                        if (nspdObj) {
                            const nspdProps = nspdObj.properties?.options || nspdObj.properties || {};
                            const localProps = obj.properties;

                            const nspdArea = parseFloat(nspdProps.area || nspdProps.params_area || nspdProps.specified_area || 0);
                            const localArea = parseFloat(localProps.area || 0);
                            
                            const diffs = [];
                            if (Math.abs(nspdArea - localArea) > 0.01) {
                                diffs.push(`Площадь: ${localArea} -> ${nspdArea}`);
                            }
                            
                            const nspdType = nspdObj.properties?.options?.category_id === '36368' ? 'plot' : 'building';
                            if (nspdType !== localProps.type) {
                                diffs.push(`Тип: ${localProps.type} -> ${nspdType}`);
                            }

                            // 3. Геометрия
                            if (nspdObj.geometry && obj.geometry) {
                                const nspdGeomStr = JSON.stringify(nspdObj.geometry.coordinates);
                                const localGeomStr = JSON.stringify(obj.geometry.coordinates);
                                if (nspdGeomStr !== localGeomStr) {
                                    diffs.push('Границы изменены');
                                }
                            }

                            if (diffs.length > 0) {
                                newUpdateStatus[cadNum] = {
                                    hasUpdate: true,
                                    newData: nspdObj,
                                    diff: diffs.join(', ')
                                };
                                updatedCount++;
                            } else {
                                newUpdateStatus[cadNum] = { hasUpdate: false };
                            }
                        }
                    }
                } catch (e) {
                    console.error(`Ошибка при проверке ${cadNum}:`, e);
                }
                
                setCheckingProgress(prev => ({ ...prev, current: i + 1 }));
                // Обновляем состояние порциями для плавности
                if (i % 3 === 0) setUpdateStatus({ ...newUpdateStatus });
            }
            
            setUpdateStatus(newUpdateStatus);
            if (updatedCount > 0) {
                toast.success(`Проверка завершена. Найдено обновлений: ${updatedCount}`);
            } else {
                toast.info('Обновлений не найдено. Все данные актуальны.');
            }
        } catch (err) {
            console.error(err);
            toast.error('Ошибка при проверке обновлений');
        } finally {
            setCheckingUpdates(false);
        }
    };

    const handleApplyAllUpdates = async () => {
        const toUpdate = Object.entries(updateStatus)
            .filter(([_, status]) => status.hasUpdate)
            .map(([_, status]) => status.newData);

        if (toUpdate.length === 0) return;

        setLoading(true);
        let successCount = 0;

        try {
            for (let i = 0; i < toUpdate.length; i++) {
                const nspdObj = toUpdate[i];
                const cadNum = nspdObj.properties.cad_num || nspdObj.properties.options?.cad_num;
                
                setImportProgress({ current: i + 1, total: toUpdate.length });
                await handleImport(nspdObj, true); // silent update
                successCount++;
                
                // Сбрасываем статус обновления для этого объекта сразу, чтобы он ушел вниз списка
                setUpdateStatus(prev => ({
                    ...prev,
                    [cadNum]: { hasUpdate: false }
                }));
                
                if (i < toUpdate.length - 1) await new Promise(r => setTimeout(r, 200));
            }
            
            toast.success(`Успешно обновлено объектов: ${successCount}`);
            setImportProgress(null);
            if (onImport) onImport();
        } catch (err) {
            console.error(err);
            toast.error('Ошибка при массовом обновлении');
        } finally {
            setLoading(false);
        }
    };

    const handleGridSearch = async (e) => {
        if (e) e.preventDefault();
        if (!quarterId) return;

        // Очищаем от пробелов и переносов для проверки типа поиска
        const inputStr = quarterId.trim();
        const isMulti = inputStr.includes(',') || inputStr.includes('\n');
        
        // Если это один номер, проверяем его формат
        let isDirectObject = false;
        let cleanId = inputStr;
        
        if (!isMulti) {
            cleanId = inputStr.endsWith(':') ? inputStr.slice(0, -1) : inputStr;
            const colonCount = (cleanId.match(/:/g) || []).length;
            isDirectObject = colonCount === 3; // XX:YY:ZZZZZZZ:NNNN
        }

        setLoading(true);
        setError(null);
        setGridResults([]);
        setProgressLogs([isDirectObject ? "Запуск прямого поиска..." : "Запуск процесса сканирования..."]);
        setStats({ total: 0, new: 0 });

        try {
            if (isDirectObject) {
                setProgressLogs(["Запрос данных конкретного объекта (прямой поиск)..."]);
                const response = await fetch(`/api/m/nspd-integration/search?q=${encodeURIComponent(cleanId)}`);
                const data = await response.json();
                
                if (data.data?.features?.[0]) {
                    const feat = data.data.features[0];
                    if (!feat.properties.cad_num) feat.properties.cad_num = cleanId;
                    
                    // Гарантируем наличие информации о типе для корректного отображения в таблице
                    if (!feat.properties.options) feat.properties.options = {};
                    if (data.object_type) {
                        feat.properties.obj_type = data.object_type;
                        feat.properties.options.obj_type = data.object_type;
                    }
                    const catId = data.meta?.[0]?.categoryId || data.data?.features?.[0]?.properties?.category_id;
                    if (catId) {
                        feat.properties.category_id = String(catId);
                        feat.properties.options.category_id = String(catId);
                    }
                    
                    setGridResults([feat]);
                    setStats({ total: 1, new: isAlreadyInLocal(cleanId) ? 0 : 1 });
                    setProgressLogs(prev => [...prev, "Объект найден.", "Процесс завершен."]);
                } else {
                    setError("Объект не найден в НСПД.");
                }
                setLoading(false);
                return;
            }

            const baseUrl = window.location.hostname === 'localhost' ? 'http://localhost:3001' : '';
            const url = isMulti 
                ? `${baseUrl}/api/m/nspd-integration/import-multiple?ids=${encodeURIComponent(inputStr)}`
                : `${baseUrl}/api/m/nspd-integration/grid?id=${encodeURIComponent(cleanId)}&type=all`;
                
            const eventSource = new EventSource(url);

            eventSource.onmessage = (event) => {
                let rawData = event.data;
                
                // На случай если префикс "data: " просочился в event.data (бывает в некоторых окружениях)
                if (typeof rawData === 'string' && rawData.startsWith('data: ')) {
                    rawData = rawData.substring(6);
                }

                let data;
                try {
                    data = JSON.parse(rawData);
                } catch (e) {
                    console.warn("NSPD SSE Parse Error:", e, rawData);
                    // Если это просто текст, добавляем в логи
                    if (rawData && rawData.length < 500) {
                        setProgressLogs(prev => [...prev.slice(-4), rawData]);
                    }
                    return;
                }

                if (data.progress) {
                    setProgressLogs(prev => [...prev.slice(-100), data.progress]);
                }

                if (data.feature) {
                    const feature = data.feature;
                    setGridResults(prev => {
                        // Избегаем дубликатов
                        if (prev.some(f => f.id === feature.id)) return prev;
                        return [...prev, feature];
                    });
                    
                    if (!isAlreadyInLocal(feature.properties?.options?.cad_num)) {
                        setStats(prev => ({ ...prev, new: prev.new + 1 }));
                        // Добавляем в логи для наглядности
                        setProgressLogs(prev => [...prev.slice(-100), `Найдено: ${feature.properties?.options?.cad_num || feature.id}`]);
                    }
                    setStats(prev => ({ ...prev, total: prev.total + 1 }));
                }

                if (data.status === 'done') {
                    setProgressLogs(prev => [...prev, `Поиск завершен. Найдено объектов: ${data.total || 0}`]);
                    eventSource.close();
                    setLoading(false);
                }

                if (data.warning) {
                    setProgressLogs(prev => [...prev, `⚠️ ${data.warning}`]);
                }

                if (data.error) {
                    setError(data.error);
                    eventSource.close();
                    setLoading(false);
                }
            };

            eventSource.onerror = (err) => {
                console.error("EventSource failed:", err);
                setError("Ошибка соединения с сервером");
                eventSource.close();
                setLoading(false);
            };

        } catch (err) {
            setError('Ошибка при запуске процесса');
            console.error(err);
            setLoading(false);
        }
    };

    const handleImportAll = async (updateOnly = false) => {
        const targetObjects = gridResults.filter(obj => {
            const inLocal = isAlreadyInLocal(obj.properties.cad_num || obj.properties.options?.cad_num);
            return updateOnly ? inLocal : !inLocal;
        });

        if (targetObjects.length === 0) {
            toast.info(updateOnly ? 'Нет объектов для обновления' : 'Нет новых объектов для импорта');
            return;
        }

        if (!window.confirm(`${updateOnly ? 'Обновить' : 'Импортировать'} ${targetObjects.length} объектов?`)) return;

        setLoading(true);
        setImportProgress({ current: 0, total: targetObjects.length });
        let successCount = 0;
        
        for (let i = 0; i < targetObjects.length; i++) {
            const obj = targetObjects[i];
            try {
                setImportProgress({ current: i + 1, total: targetObjects.length });
                await handleImport(obj, true);
                successCount++;
            } catch (err) {
                console.error(`Ошибка при обработке ${obj.properties.cad_num}:`, err);
            }
        }
        
        setLoading(false);
        setImportProgress(null);
        toast.success(`Успешно обработано ${successCount} объектов`);
        if (onImport) onImport();
    };

    const handleImport = async (targetObj, silent = false) => {
        const obj = targetObj || (result && result.data?.features?.[0]);
        if (!obj) {
            toast.error('Нет данных для импорта');
            return;
        }

        const cadNum = obj.properties?.cad_num || result?.kad_number || obj.properties?.options?.cad_num;
        
        // Поиск существующего объекта в локальной базе по кадастровому номеру
        const existingFeature = mapData?.features?.find(f => f.properties.cadastral_number === cadNum);
        
        const getImportType = (feature) => {
            const p = feature.properties || {};
            const opt = p.options || {};
            const catId = String(p.category_id || opt.category_id || '');
            const ot = (p.obj_type || opt.obj_type || '').toLowerCase();

            if (catId === '36368' || ot === 'зу' || ot.includes('участок')) return 'plot';
            if (catId === '36369' || ot === 'здание' || ot === 'окс' || ot.includes('здание')) return 'building';
            if (catId === '36383' || ot.includes('сооружение')) return 'building'; 
            return 'plot';
        };

        const newFeature = {
            id: existingFeature ? existingFeature.id : crypto.randomUUID(),
            type: 'Feature',
            geometry: obj.geometry,
            properties: {
                ...(existingFeature ? existingFeature.properties : {}),
                // Обновляем технические поля из НСПД
                cadastral_number: cadNum,
                area: obj.properties?.area_value || obj.properties?.options?.area_value || 0,
                address: obj.properties?.address || obj.properties?.options?.address || '',
                nspd_data: obj.properties || obj.properties?.options || {},
                // Сохраняем тип если он уже есть, иначе определяем из НСПД
                type: existingFeature?.properties?.type || getImportType(obj),
                // Сохраняем категорию если она уже есть, иначе ставим дефолтную для типа
                category: existingFeature?.properties?.category || (getImportType(obj) === 'plot' ? 'plot_public' : 'building_residential'),
                // Сохраняем имя если оно уже есть, иначе ставим из НСПД
                name: existingFeature?.properties?.name || (obj.properties?.descr || cadNum || 'Объект НСПД'),
            }
        };

        try {
            const response = await fetch('/api/feature', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(newFeature)
            });

            if (response.ok) {
                if (!silent) {
                    toast.success(`Объект ${cadNum} импортирован`);
                    if (onImport) onImport();
                }
            } else {
                throw new Error('Ошибка при сохранении');
            }
        } catch (err) {
            if (!silent) toast.error('Не удалось импортировать объект');
            console.error(err);
            if (silent) throw err; // Re-throw for handleImportAll
        }
    };

    const isAlreadyInLocal = (cadastralNumber) => {
        return !!getLocalMatch(cadastralNumber);
    };

    if (!isVisible) return null;

    return (
        <div className={styles.modalOverlay} onClick={onClose}>
            <div className={styles.modalContent} onClick={e => e.stopPropagation()}>
                <div className={styles.header}>
                    <div className={styles.title}>
                        <GlobeIcon size={24} />
                        <span>Мастер импорта НСПД</span>
                    </div>
                    <button className={styles.closeBtn} onClick={onClose}>
                        <XCircleIcon size={24} />
                    </button>
                </div>

                <div className={styles.layout}>
                    <div className={styles.sidebar}>
                        <div className={styles.navGroup}>
                            <button 
                                className={`${styles.navItem} ${searchMode === 'grid' ? styles.active : ''}`}
                                onClick={() => setSearchMode('grid')}
                            >
                                <LayersIcon size={20} />
                                <div>
                                    <div className={styles.navLabel}>Импорт</div>
                                    <div className={styles.navDesc}>По номеру</div>
                                </div>
                            </button>
                            <button 
                                className={`${styles.navItem} ${searchMode === 'objects' ? styles.active : ''}`}
                                onClick={() => setSearchMode('objects')}
                            >
                                <CheckCircleIcon size={20} />
                                <div>
                                    <div className={styles.navLabel}>Объекты</div>
                                    <div className={styles.navDesc}>Сохраненные в базе</div>
                                </div>
                            </button>
                            <button 
                                className={`${styles.navItem} ${searchMode === 'point' ? styles.active : ''}`}
                                onClick={() => setSearchMode('point')}
                            >
                                <CrosshairIcon size={20} />
                                <div>
                                    <div className={styles.navLabel}>Поиск на карте</div>
                                    <div className={styles.navDesc}>Выбор точки кликом</div>
                                </div>
                            </button>
                        </div>

                        <div className={styles.sidebarActions}>
                            <button className={styles.mapSelectBtn} onClick={onMapSelectRequest}>
                                <CrosshairIcon size={18} />
                                <span>Указать точку на карте</span>
                            </button>
                        </div>
                    </div>

                    <div className={styles.main}>
                        {searchMode === 'grid' && (
                            <div className={styles.viewSection}>
                                <div className={styles.sectionHeader}>
                                    <div>
                                        <h2>Импорт по кадастровому номеру</h2>
                                        <p className={styles.viewDesc}>Укажите один или несколько номеров через запятую или с новой строки. Также можно указать номер квартала для полной загрузки.</p>
                                    </div>
                                    <div className={styles.sectionActions}>
                                        {gridResults.length > 0 && (
                                            <div className={styles.bulkActions}>
                                                <button className={styles.updateAllBtn} onClick={() => handleImportAll(true)} disabled={loading}>
                                                    <RotateIcon size={18} />
                                                    <span>Обновить все ({gridResults.filter(o => isAlreadyInLocal(o.properties.cad_num || o.properties.options?.cad_num)).length})</span>
                                                </button>
                                                <button className={styles.importAllBtn} onClick={() => handleImportAll(false)} disabled={loading}>
                                                    <PlusIcon size={18} />
                                                    <span>Импорт новых ({stats.new})</span>
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                                
                                <form className={styles.searchForm} onSubmit={handleGridSearch}>
                                    <div className={styles.inputRow}>
                                        <textarea 
                                            placeholder="Введите кадастровые номера...&#10;Напр: 16:42:201103:22, 16:42:201103:45"
                                            value={cadNumbers || quarterId}
                                            onChange={(e) => {
                                                setCadNumbers(e.target.value);
                                                setQuarterId(e.target.value);
                                            }}
                                            className={styles.mainTextarea}
                                            rows={3}
                                        />
                                        <button type="submit" className={styles.mainSearchBtn} disabled={loading || extracting}>
                                            {loading ? 'Поиск...' : 'Найти объекты'}
                                        </button>
                                    </div>
                                    <div className={styles.uploadRow}>
                                        <label className={styles.uploadLabel}>
                                            <UploadIcon size={18} />
                                            <span>{extracting ? 'Обработка...' : 'Загрузить архив выгрузки (ZIP)'}</span>
                                            <input 
                                                type="file" 
                                                accept=".zip" 
                                                onChange={handleFileUpload} 
                                                className={styles.hiddenInput}
                                                disabled={extracting || loading}
                                            />
                                        </label>
                                        <p className={styles.uploadHint}>Система автоматически извлечет кадастровые номера из CSV файлов в архиве</p>
                                    </div>
                                </form>

                                {loading && (
                                    <div className={styles.progressConsole}>
                                        <div className={styles.consoleHeader}>
                                            <div className={styles.pulse} />
                                            <span>
                                                {importProgress 
                                                    ? `Импорт объектов: ${importProgress.current} из ${importProgress.total}...` 
                                                    : `Прогресс поиска... Найдено: ${stats.total}`}
                                            </span>
                                        </div>
                                        <div className={styles.consoleContent} ref={consoleRef}>
                                            {progressLogs.map((log, i) => (
                                                <div key={i} className={styles.logEntry}>
                                                    <span className={styles.logTime}>{new Date().toLocaleTimeString()}</span>
                                                    <span className={styles.logText}>{log}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}


                                {gridResults.length > 0 && (
                                    <div className={styles.gridTableContainer}>
                                        <table className={styles.gridTable}>
                                            <thead>
                                                <tr>
                                                    <th>Тип</th>
                                                    <th>Кадастровый номер</th>
                                                    <th>Статус в базе</th>
                                                    <th>Действие</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {gridResults.map(obj => {
                                                    const cad = obj.properties.cad_num || obj.properties.options?.cad_num;
                                                    const inLocal = isAlreadyInLocal(cad);
                                                    
                                                    const getDisplayType = (feature) => {
                                                        const p = feature.properties || {};
                                                        const opt = p.options || {};
                                                        const catId = String(p.category_id || opt.category_id || '');
                                                        const ot = (p.obj_type || opt.obj_type || '').toLowerCase();
                                                        
                                                        if (catId === '36368' || ot === 'зу' || ot.includes('участок')) return 'Участок';
                                                        if (catId === '36369' || ot === 'здание' || ot === 'окс' || ot.includes('здание')) return 'Здание';
                                                        if (catId === '36383' || ot.includes('сооружение')) return 'Сооружение';
                                                        return 'Участок';
                                                    };
                                                    
                                                    const type = getDisplayType(obj);
                                                    
                                                    return (
                                                        <tr key={obj.id || cad}>
                                                            <td>
                                                                <span className={type === 'Участок' ? styles.typePlot : styles.typeBuilding}>
                                                                    {type}
                                                                </span>
                                                            </td>
                                                            <td className={styles.cadCell}>{cad}</td>
                                                            <td>
                                                                {inLocal ? (
                                                                    <span className={styles.statusInLocal}>✓ В базе</span>
                                                                ) : (
                                                                    <span className={styles.statusNew}>Новый</span>
                                                                )}
                                                            </td>
                                                            <td>
                                                                <div className={styles.actionCell}>
                                                                    <button 
                                                                        className={inLocal ? styles.updateBtn : styles.importMiniBtn}
                                                                        onClick={() => handleImport(obj)}
                                                                    >
                                                                        {inLocal ? 'Обновить' : 'Импорт'}
                                                                    </button>
                                                                    {inLocal && (
                                                                        <button 
                                                                            className={styles.editBtn}
                                                                            title="Перейти к редактированию"
                                                                            onClick={() => {
                                                                                const localObj = getLocalMatch(cad);
                                                                                if (localObj && onFeatureSelect) {
                                                                                    onFeatureSelect(localObj);
                                                                                    onClose();
                                                                                }
                                                                            }}
                                                                        >
                                                                            <SettingsIcon size={14} />
                                                                        </button>
                                                                    )}
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        )}

                        {searchMode === 'objects' && (
                            <div className={styles.viewSection}>
                                <div className={styles.sectionHeader}>
                                    <div>
                                        <h2>Сохраненные объекты ({savedObjects.length})</h2>
                                        <p className={styles.viewDesc}>Список объектов в вашей базе. Вы можете проверить их на наличие обновлений в НСПД.</p>
                                    </div>
                                    <div className={styles.sectionActions}>
                                        {checkingUpdates && (
                                            <div className={styles.checkProgress}>
                                                Проверка: {checkingProgress.current} из {checkingProgress.total}
                                            </div>
                                        )}
                                        <button 
                                            className={styles.checkUpdatesBtn} 
                                            onClick={handleCheckAllUpdates} 
                                            disabled={checkingUpdates || loading}
                                        >
                                            <SearchIcon size={18} />
                                            <span>{checkingUpdates ? 'Проверка...' : 'Проверить обновления'}</span>
                                        </button>
                                        
                                        {Object.values(updateStatus).some(s => s.hasUpdate) && (
                                            <button 
                                                className={styles.importAllBtn} 
                                                onClick={handleApplyAllUpdates} 
                                                disabled={loading}
                                            >
                                                <RotateIcon size={18} />
                                                <span>Применить все обновления</span>
                                            </button>
                                        )}
                                    </div>
                                </div>

                                <div className={styles.gridTableContainer}>
                                    <table className={styles.gridTable}>
                                        <thead>
                                            <tr>
                                                <th>Наименование</th>
                                                <th>Кадастровый номер</th>
                                                <th>Тип</th>
                                                <th>Статус / Изменения</th>
                                                <th>Действие</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {savedObjects.length > 0 ? [...savedObjects].sort((a, b) => {
                                                const statusA = updateStatus[a.properties.cadastral_number]?.hasUpdate ? 1 : 0;
                                                const statusB = updateStatus[b.properties.cadastral_number]?.hasUpdate ? 1 : 0;
                                                return statusB - statusA;
                                            }).map(obj => {
                                                const cad = obj.properties.cadastral_number;
                                                const status = updateStatus[cad];
                                                
                                                return (
                                                    <tr key={obj.id} className={status?.hasUpdate ? styles.rowWithUpdate : ''}>
                                                        <td>{obj.properties.name || 'Без имени'}</td>
                                                        <td className={styles.cadCell}>{cad}</td>
                                                        <td>
                                                            <span className={obj.properties.type === 'plot' ? styles.typePlot : styles.typeBuilding}>
                                                                {obj.properties.type === 'plot' ? 'Участок' : 'Здание'}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            {status?.hasUpdate ? (
                                                                <div className={styles.updateBadge}>
                                                                    <AlertTriangleIcon size={14} />
                                                                    <span>{status.diff}</span>
                                                                </div>
                                                            ) : status ? (
                                                                <span className={styles.statusOk}>Актуально</span>
                                                            ) : (
                                                                <span className={styles.statusUnknown}>Не проверено</span>
                                                            )}
                                                        </td>
                                                        <td>
                                                            <button 
                                                                className={status?.hasUpdate ? styles.importMiniBtn : styles.updateBtn}
                                                                onClick={() => {
                                                                    if (status?.hasUpdate) {
                                                                        handleImport(status.newData, false);
                                                                    } else {
                                                                        setSearchMode('point');
                                                                        setCadNumber(cad);
                                                                        handleSearch(null, cad);
                                                                    }
                                                                }}
                                                                disabled={loading || checkingUpdates}
                                                            >
                                                                <RotateIcon size={14} />
                                                                <span>{status?.hasUpdate ? 'Применить' : 'Обновить'}</span>
                                                            </button>
                                                        </td>
                                                    </tr>
                                                );
                                            }) : (
                                                <tr>
                                                    <td colSpan="5" className={styles.emptyRow}>Сохраненных объектов с кадастровыми номерами пока нет.</td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}

                        {searchMode === 'point' && (
                            <div className={styles.viewSection}>
                                <h2>Результаты по выбранной точке</h2>
                                
                                {loading ? (
                                    <div className={styles.viewLoading}>
                                        <RotateIcon size={40} className={styles.spin} />
                                        <p>Запрашиваем данные из НСПД...</p>
                                    </div>
                                ) : result ? (
                                    <div className={styles.objectDetail}>
                                        {result.multiple ? (
                                            <div className={styles.multipleResults}>
                                                <h3>Найдено объектов: {result.items.length}</h3>
                                                <div className={styles.resultsList}>
                                                    {result.items.map((item, idx) => (
                                                        <div key={idx} className={styles.resultItem}>
                                                            <div className={styles.itemHeader}>
                                                                <div className={styles.typeWithCheck}>
                                                                    <span className={styles.itemType}>
                                                                        {item.object_type || item.data?.features?.[0]?.properties?.obj_type || 'Объект'}
                                                                    </span>
                                                                    {isAlreadyInLocal(item.kad_number) && (
                                                                        <CheckCircleIcon size={14} color="#10b981" title="Уже в базе" />
                                                                    )}
                                                                </div>
                                                                <span className={styles.itemNum}>{item.kad_number}</span>
                                                            </div>
                                                            <button 
                                                                className={
                                                                    getItemStatus(item).type === 'current' ? styles.currentBtn :
                                                                    getItemStatus(item).type === 'update' ? styles.miniUpdateBtn : 
                                                                    styles.miniImportBtn
                                                                } 
                                                                onClick={() => handleImport(item)}
                                                                disabled={getItemStatus(item).type === 'current'}
                                                            >
                                                                {getItemStatus(item).icon}
                                                                <span>{getItemStatus(item).label}</span>
                                                            </button>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        ) : localMatch ? (
                                            <div className={styles.comparisonContainer}>
                                                <div className={styles.comparisonHeader}>
                                                    <CheckCircleIcon size={20} color="#10b981" />
                                                    <h3>Найдено совпадение в вашей базе</h3>
                                                </div>
                                                <table className={styles.compareTable}>
                                                    <thead>
                                                        <tr>
                                                            <th>Поле</th>
                                                            <th>Локальное значение</th>
                                                            <th>Данные НСПД</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        <tr>
                                                            <td>Имя</td>
                                                            <td>{localMatch.properties.name}</td>
                                                            <td className={styles.nspdVal}>{result.data?.features?.[0]?.properties?.descr || '-'}</td>
                                                        </tr>
                                                        <tr>
                                                            <td>Площадь</td>
                                                            <td>{localMatch.properties.area || '-'} м²</td>
                                                            <td className={styles.nspdVal}>{result.data?.features?.[0]?.properties?.area_value} м²</td>
                                                        </tr>
                                                        <tr>
                                                            <td>Адрес</td>
                                                            <td>{localMatch.properties.address || '-'}</td>
                                                            <td className={styles.nspdVal}>{result.data?.features?.[0]?.properties?.address || '-'}</td>
                                                        </tr>
                                                        <tr>
                                                            <td>Геометрия</td>
                                                            <td><span className={styles.geoStatus}>Существует</span></td>
                                                            <td className={styles.nspdVal}><span className={styles.geoUpdate}>Будет обновлена</span></td>
                                                        </tr>
                                                    </tbody>
                                                </table>
                                                <div className={styles.comparisonActions}>
                                                    <button className={styles.bigImportBtn} onClick={() => handleImport()}>
                                                        <RotateIcon size={20} />
                                                        <span>Обновить геометрию и данные</span>
                                                    </button>
                                                    <button 
                                                        className={styles.bigEditBtn} 
                                                        onClick={() => {
                                                            if (onFeatureSelect) {
                                                                onFeatureSelect(localMatch);
                                                                onClose();
                                                            }
                                                        }}
                                                    >
                                                        <SettingsIcon size={20} />
                                                        <span>Перейти к редактированию</span>
                                                    </button>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className={styles.detailCard}>
                                                <div className={styles.detailHeader}>
                                                    <div className={styles.detailType}>{result.object_type || 'Объект'}</div>
                                                    <div className={styles.detailNum}>{result.kad_number}</div>
                                                </div>
                                                <div className={styles.detailBody}>
                                                    <div className={styles.detailRow}>
                                                        <span className={styles.detailLabel}>Адрес:</span>
                                                        <span className={styles.detailValue}>{result.data?.features?.[0]?.properties?.address || 'Не указан'}</span>
                                                    </div>
                                                    <div className={styles.detailRow}>
                                                        <span className={styles.detailLabel}>Площадь:</span>
                                                        <span className={styles.detailValue}>{result.data?.features?.[0]?.properties?.area_value} м²</span>
                                                    </div>
                                                </div>
                                                <button className={styles.bigImportBtn} onClick={() => handleImport()}>
                                                    <PlusIcon size={20} />
                                                    <span>Импортировать на карту</span>
                                                </button>
                                            </div>
                                        )}

                                        <div className={styles.localCompare}>
                                            <h3>Совпадения в нашей базе (в радиусе 20м):</h3>
                                            {localObjectsAtPoint.length > 0 ? (
                                                <div className={styles.localGrid}>
                                                    {localObjectsAtPoint.map(obj => (
                                                        <div 
                                                            key={obj.id} 
                                                            className={styles.localCard}
                                                            onClick={() => {
                                                                if (onFeatureSelect) {
                                                                    onFeatureSelect(obj);
                                                                    onClose();
                                                                }
                                                            }}
                                                        >
                                                            <div className={styles.localCardHeader}>
                                                                <span className={obj.properties.type === 'plot' ? styles.typePlot : styles.typeBuilding}>
                                                                    {obj.properties.type === 'plot' ? 'Участок' : 'Здание'}
                                                                </span>
                                                                <span className={styles.localId}>{obj.properties.cadastral_number}</span>
                                                            </div>
                                                            <div className={styles.localName}>{obj.properties.name || 'Без имени'}</div>
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : (
                                                <div className={styles.emptyMsg}>Локальных объектов в этой точке нет.</div>
                                            )}
                                        </div>
                                    </div>
                                ) : (
                                    <div className={styles.emptyView}>
                                        <CrosshairIcon size={60} />
                                        <p>Нажмите «Указать точку на карте» и кликните на интересующий участок</p>
                                    </div>
                                )}
                            </div>
                        )}

                        {error && (
                            <div className={styles.errorBox}>
                                <AlertTriangleIcon size={24} />
                                <div>
                                    <div className={styles.errorTitle}>Ошибка запроса</div>
                                    <div className={styles.errorMsg}>{error}</div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default NSPDPanel;
